/**
 * ConversionExamplesTable.js — Tabla de Conversiones publicable.
 *
 * Reproduce el layout aprobado por el CEO (3 cards lado a lado: CUP, MLC, USD)
 * con ejemplos concretos de montos populares (500/1k/2k/5k/10k UYU → destino).
 * Pensada para que el CEO, Gestor o Proveedor CU haga screenshot y la publique
 * en sus redes / WhatsApp como referencia de precios.
 *
 * Tasas en vivo: lee `corredores.<CCY>.tasa_cliente` de `/api/usdt-config`
 * (mismo source que `calc_remesa`). Se actualiza al instante cuando el CEO o
 * el Proveedor CU edita "Zona de Cambios".
 */
import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Calendar, ArrowRight, Share2, Download } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { toPng } from 'html-to-image';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Montos sugeridos por corredor — pensados para que los números destino sean
// "redondos" y publicables. USD usa montos UYU más altos porque la tasa es
// muy baja (~0.021).
const ROWS_BY_CCY = {
  CUP: [500, 1000, 2000, 5000, 10000],
  MLC: [500, 1000, 2000, 5000, 10000],
  USD: [1000, 2000, 5000, 10000, 20000],
};

// Decimales por moneda destino (CUP/MLC mostramos sin decimales, USD con 0).
const DECIMALS = { CUP: 0, MLC: 0, USD: 0 };

const PILL_STYLES = {
  CUP: { bg: 'bg-amber-100',  text: 'text-amber-800',  ring: 'ring-amber-300',  label: 'Peso Cubano',  accent: 'border-amber-400' },
  MLC: { bg: 'bg-violet-100', text: 'text-violet-800', ring: 'ring-violet-300', label: 'MLC',          accent: 'border-violet-400' },
  USD: { bg: 'bg-rose-100',   text: 'text-rose-800',   ring: 'ring-rose-300',   label: 'USD',          accent: 'border-rose-400' },
};

const fmtUYU = (n) =>
  `$ ${Number(n).toLocaleString('es-UY', { useGrouping: true })} UYU`;
const fmtDest = (n, ccy) =>
  `${Number(n).toLocaleString('es-UY', {
    useGrouping: true,
    minimumFractionDigits: DECIMALS[ccy] ?? 0,
    maximumFractionDigits: DECIMALS[ccy] ?? 0,
  })} ${ccy}`;

const CorridorCard = ({ ccy, tasa }) => {
  const style = PILL_STYLES[ccy];
  const rows = ROWS_BY_CCY[ccy] || [];
  return (
    <Card
      className={`border-0 shadow-sm overflow-hidden ${style.accent} border-t-4`}
      data-testid={`conv-card-${ccy}`}
    >
      {/* Pill: UYU → CCY */}
      <div className="flex items-center justify-between px-4 pt-4">
        <div className="inline-flex items-center gap-1.5">
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-amber-50 text-amber-900 text-[11px] font-bold ring-1 ring-amber-200">
            UYU
          </span>
          <ArrowRight className="w-3.5 h-3.5 text-slate-400" />
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full ${style.bg} ${style.text} text-[11px] font-bold ring-1 ${style.ring}`}
          >
            {ccy}
          </span>
        </div>
        <span className="text-[11px] font-semibold text-slate-500">{style.label}</span>
      </div>

      {/* Filas */}
      <div className="px-4 py-3">
        {tasa > 0 ? (
          <table className="w-full">
            <tbody>
              {rows.map((amt) => (
                <tr key={amt} className="border-b last:border-0 border-slate-100">
                  <td className="py-2.5 text-sm text-slate-700 font-mono">{fmtUYU(amt)}</td>
                  <td className="py-2.5 text-sm text-slate-900 font-mono font-bold text-right">
                    {fmtDest(amt * tasa, ccy)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-xs text-slate-400 py-4 text-center">
            Corredor no disponible
          </p>
        )}
      </div>
    </Card>
  );
};

/**
 * Tabla de Conversiones con ejemplos.
 *
 * Props:
 *   getAuthHeaders: () => headers — passed in by parent (auth-aware).
 *   sendPath?: ruta a "Enviar Remesa" para el botón CTA.
 */
const ConversionExamplesTable = ({ getAuthHeaders, sendPath = '/remesas/enviar' }) => {
  const [cfg, setCfg] = useState(null);
  const [tab, setTab] = useState('normal');
  const [sharing, setSharing] = useState(false);
  const shareRef = useRef(null);

  useEffect(() => {
    let alive = true;
    let timer = null;
    const fetchCfg = async () => {
      try {
        const r = await axios.get(`${API}/usdt-config`, { headers: getAuthHeaders?.() || {} });
        if (alive) setCfg(r.data);
      } catch { /* silent */ }
    };
    fetchCfg();
    // Iter 64 · refresh cada 30s para reflejar cambios en "Zona de Cambios"
    timer = setInterval(fetchCfg, 30_000);
    return () => { alive = false; if (timer) clearInterval(timer); };
  }, [getAuthHeaders]);

  const tasaCcy = (ccy) =>
    Number(cfg?.corredores?.[ccy]?.tasa_cliente ?? 0);

  /**
   * Genera un PNG nítido (pixelRatio=2) del bloque `shareRef` — solo título,
   * pills y los 3 cards (sin sidebar ni header). Si el navegador soporta
   * Web Share API con archivos (móvil moderno), abre el share sheet
   * (WhatsApp / Instagram / Telegram). Si no, descarga el PNG localmente.
   */
  const handleShare = async () => {
    if (!shareRef.current || sharing) return;
    setSharing(true);
    try {
      const dataUrl = await toPng(shareRef.current, {
        pixelRatio: 2,
        backgroundColor: '#ffffff',
        cacheBust: true,
        filter: (node) => !(node?.dataset?.skipExport === 'true'),
      });
      const blob = await (await fetch(dataUrl)).blob();
      const fileName = `aircom-tasas-${new Date().toISOString().slice(0, 10)}.png`;
      const file = new File([blob], fileName, { type: 'image/png' });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: 'Aircom · Tasas de remesas',
          text: '💵 Tasas actuales para enviar dinero a Cuba · Aircom Connect',
        });
        toast.success('Imagen lista para compartir');
      } else {
        const link = document.createElement('a');
        link.href = dataUrl;
        link.download = fileName;
        link.click();
        toast.success('Imagen descargada · lista para enviar por WhatsApp');
      }
    } catch (err) {
      // AbortError = user cancelled the native share sheet (no toast needed).
      if (err?.name !== 'AbortError') {
        toast.error('No se pudo generar la imagen');
      }
    } finally {
      setSharing(false);
    }
  };

  return (
    <section className="space-y-4" data-testid="conversion-examples-table">
      {/* Bloque exportable — sin sidebar, sin header del shell */}
      <div
        ref={shareRef}
        className="bg-white px-4 pt-6 pb-4 rounded-xl"
        data-testid="conv-share-block"
      >
        <div className="text-center">
          <span className="inline-flex items-center gap-1.5 text-xs font-bold text-teal-700 uppercase tracking-wider">
            <Calendar className="w-3.5 h-3.5" />
            Tasas actuales
          </span>
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mt-1">
            Tabla de Conversiones
          </h2>
          <p className="text-sm text-slate-500 mt-1.5">
            Consulta las tasas de cambio actuales para enviar remesas a Cuba
          </p>
        </div>

        {/* Tabs Tasas Normales / Promo Depósito Bancario */}
        <div className="flex items-center justify-center mt-4" data-testid="conv-tabs" data-skip-export="true">
          <div className="inline-flex rounded-full border border-slate-200 bg-slate-50 p-1">
            <button
              type="button"
              onClick={() => setTab('normal')}
              data-testid="conv-tab-normal"
              className={`px-4 py-1.5 text-xs font-semibold rounded-full transition-all ${
                tab === 'normal'
                  ? 'bg-teal-600 text-white shadow'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              Tasas Normales
            </button>
            <button
              type="button"
              onClick={() => setTab('promo')}
              data-testid="conv-tab-promo"
              className={`px-4 py-1.5 text-xs font-semibold rounded-full transition-all ${
                tab === 'promo'
                  ? 'bg-teal-600 text-white shadow'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              Promo Depósito Bancario
            </button>
          </div>
        </div>

        {/* 3 cards lado a lado */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          <CorridorCard ccy="CUP" tasa={tasaCcy('CUP')} />
          <CorridorCard ccy="MLC" tasa={tasaCcy('MLC')} />
          <CorridorCard ccy="USD" tasa={tasaCcy('USD')} />
        </div>

        {tab === 'promo' && (
          <p className="text-center text-xs text-slate-500 italic mt-3">
            Promociones por depósito bancario · contacta al equipo para condiciones especiales.
          </p>
        )}

        {/* Footer con branding (visible solo en el screenshot exportado) */}
        <div className="flex items-center justify-between mt-5 pt-3 border-t border-slate-100">
          <p className="text-[11px] text-slate-500 font-semibold">
            Aircom Connect · Send Money 🇺🇾 → 🇨🇺
          </p>
          {cfg?.updated_at && (
            <p className="text-[10px] text-slate-400">
              Actualizado: {new Date(cfg.updated_at).toLocaleString('es-UY', { dateStyle: 'short', timeStyle: 'short' })}
            </p>
          )}
        </div>
      </div>

      {/* Botones de acción — NO se incluyen en el screenshot */}
      <div className="flex flex-wrap justify-center gap-3 pt-1" data-skip-export="true">
        <Button
          onClick={handleShare}
          disabled={sharing || !cfg}
          className="bg-teal-600 hover:bg-teal-700 text-white font-semibold"
          data-testid="conv-share-btn"
        >
          {sharing ? (
            <>
              <Download className="w-4 h-4 mr-2 animate-pulse" />
              Generando…
            </>
          ) : (
            <>
              <Share2 className="w-4 h-4 mr-2" />
              Compartir tabla
            </>
          )}
        </Button>
        <Link to={sendPath}>
          <Button
            variant="outline"
            className="border-teal-300 text-teal-700 hover:bg-teal-50 font-semibold"
            data-testid="conv-cta-converter"
          >
            <Calendar className="w-4 h-4 mr-2" />
            Ir al Conversor de Divisas
          </Button>
        </Link>
      </div>
    </section>
  );
};

export default ConversionExamplesTable;
